import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from "@angular/common";
import {TutorialProgressStateInterface} from "./models/tutorial-progress-state.interface";
import {TutorialSettingsInterface} from "../../api/interfaces/tutorial-settings.interface";
import {TutorialCampaignProgressInterface} from "../../api/interfaces/tutorial-campaign-progress.interface";
import {StorageService} from "../services/storage.service";
import {CurrentUserViewModel} from "../models/current-user.view-model";
import {Observable, of} from "rxjs";
import {catchError, map, switchMap, take, tap} from "rxjs/operators";
import {TokenDataProviderService} from "../services/token-data-provider.service";
import {UserSettingsWriterService} from "../services/user-settings-writer.service";
import {UserApiModel} from "../../api/models/user.api-model";
import {TutorialCampaignDefinition} from "./models/tutorial-campaign-definition.interface";

@Injectable({
  providedIn: 'root'
})
export class TutorialStateService {
  private readonly storageKey = 'wtg.tutorials.state.v1';

  constructor(
    private readonly storage: StorageService,
    private readonly tokenDPS: TokenDataProviderService,
    private readonly settingsWriter: UserSettingsWriterService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {
  }

  public getLocalState(): TutorialProgressStateInterface {
    if (!isPlatformBrowser(this.platformId)) {
      return {campaigns: {}};
    }

    const raw = this.storage.getItem(this.storageKey);
    if (!raw) {
      return {campaigns: {}};
    }

    try {
      const parsed = JSON.parse(raw);
      return {
        campaigns: parsed?.campaigns || {},
      };
    } catch {
      return {campaigns: {}};
    }
  }

  public getRemoteState(settings?: TutorialSettingsInterface | null): TutorialProgressStateInterface {
    return {
      campaigns: settings?.campaigns || {},
    };
  }

  public getMergedState(settings?: TutorialSettingsInterface | null): TutorialProgressStateInterface {
    const merged = this.mergeStates(this.getRemoteState(settings), this.getLocalState());
    this.persistLocalState(merged);

    return merged;
  }

  public isCampaignCompleted(
    state: TutorialProgressStateInterface,
    campaign: TutorialCampaignDefinition,
  ): boolean {
    const progress = state?.campaigns?.[campaign.key];

    return !!progress && (progress.version || 0) >= campaign.version;
  }

  public recordCampaignProgress(
    campaignKey: string,
    version: number,
    status: 'completed' | 'skipped',
  ): Observable<TutorialProgressStateInterface> {
    return this.tokenDPS.currentUser.pipe(
      take(1),
      switchMap((currentUser: CurrentUserViewModel) => {
        const currentState = this.getMergedState(currentUser?.settings?.tutorials);
        const updatedState = this.withCampaignProgress(currentState, campaignKey, {
          version,
          status,
          completedAt: new Date().toISOString(),
        });

        this.persistLocalState(updatedState);

        if (!currentUser?.['@id']) {
          return of(updatedState);
        }

        return this.settingsWriter.update(settings => {
          const latest = this.getMergedState(settings.tutorials);
          return {
            ...settings,
            tutorials: {campaigns: this.withCampaignProgress(latest, campaignKey, {
              version, status, completedAt: updatedState.campaigns[campaignKey].completedAt,
            }).campaigns},
          };
        }).pipe(
          tap((userApiModel: UserApiModel) => this.persistLocalState(
            this.mergeStates(this.getRemoteState(userApiModel.settings?.tutorials), updatedState)
          )),
          map((userApiModel: UserApiModel) => this.getMergedState(userApiModel.settings?.tutorials)),
          catchError(() => of(updatedState)),
        );
      }),
    );
  }

  public syncStateWithCurrentUser(): Observable<TutorialProgressStateInterface> {
    return this.tokenDPS.currentUser.pipe(
      take(1),
      switchMap((currentUser: CurrentUserViewModel) => {
        const mergedState = this.getMergedState(currentUser?.settings?.tutorials);
        const remoteState = this.getRemoteState(currentUser?.settings?.tutorials);

        if (!currentUser?.['@id'] || this.serializeState(mergedState) === this.serializeState(remoteState)) {
          return of(mergedState);
        }

        return this.settingsWriter.update(settings => ({
          ...settings,
          tutorials: {campaigns: this.getMergedState(settings.tutorials).campaigns},
        })).pipe(
          tap((userApiModel: UserApiModel) => this.persistLocalState(
            this.mergeStates(this.getRemoteState(userApiModel.settings?.tutorials), mergedState)
          )),
          map((userApiModel: UserApiModel) => this.getMergedState(userApiModel.settings?.tutorials)),
          catchError(() => of(mergedState)),
        );
      }),
    );
  }

  public resetAllProgress(): Observable<TutorialProgressStateInterface> {
    return this.resetCampaignProgress(Object.keys(this.getLocalState().campaigns || {}));
  }

  public resetCampaignProgress(campaignKeys: string[]): Observable<TutorialProgressStateInterface> {
    const currentLocalState = this.getLocalState();
    const nextCampaigns = {...(currentLocalState.campaigns || {})};

    campaignKeys.forEach((campaignKey) => {
      delete nextCampaigns[campaignKey];
    });

    const resetState: TutorialProgressStateInterface = {campaigns: nextCampaigns};
    this.persistLocalState(resetState);

    return this.tokenDPS.currentUser.pipe(
      take(1),
      switchMap((currentUser: CurrentUserViewModel) => {
        if (!currentUser?.['@id']) {
          return of(resetState);
        }

        return this.settingsWriter.update(settings => {
          const campaigns = {...this.getMergedState(settings.tutorials).campaigns};
          campaignKeys.forEach(key => delete campaigns[key]);
          return {...settings, tutorials: {campaigns}};
        }).pipe(
          tap((userApiModel: UserApiModel) => this.persistLocalState(
            this.getRemoteState(userApiModel.settings?.tutorials)
          )),
          map(() => resetState),
        );
      }),
    );
  }

  private persistLocalState(state: TutorialProgressStateInterface): void {
    this.storage.setItem(this.storageKey, JSON.stringify(state));
  }

  private mergeStates(
    remote: TutorialProgressStateInterface,
    local: TutorialProgressStateInterface,
  ): TutorialProgressStateInterface {
    const mergedCampaigns: Record<string, TutorialCampaignProgressInterface> = {};
    const keys = new Set([
      ...Object.keys(remote?.campaigns || {}),
      ...Object.keys(local?.campaigns || {}),
    ]);

    keys.forEach((key) => {
      mergedCampaigns[key] = this.pickNewerProgress(remote?.campaigns?.[key], local?.campaigns?.[key]);
    });

    return {campaigns: mergedCampaigns};
  }

  private pickNewerProgress(
    remote?: TutorialCampaignProgressInterface,
    local?: TutorialCampaignProgressInterface,
  ): TutorialCampaignProgressInterface {
    if (!remote) {
      return local || {};
    }
    if (!local) {
      return remote;
    }

    const remoteVersion = remote.version || 0;
    const localVersion = local.version || 0;

    if (remoteVersion !== localVersion) {
      return remoteVersion > localVersion ? remote : local;
    }

    const remoteTimestamp = new Date(remote.completedAt || 0).getTime();
    const localTimestamp = new Date(local.completedAt || 0).getTime();

    return remoteTimestamp >= localTimestamp ? remote : local;
  }

  private withCampaignProgress(
    state: TutorialProgressStateInterface,
    campaignKey: string,
    progress: TutorialCampaignProgressInterface,
  ): TutorialProgressStateInterface {
    return {
      campaigns: {
        ...state.campaigns,
        [campaignKey]: progress,
      },
    };
  }

  private serializeState(state: TutorialProgressStateInterface): string {
    return JSON.stringify(state);
  }

}
